import React from 'react';
import { getCountryFlag } from '../../../shared/countryFlag';
import { getTransportEmoji } from '../../../shared/transport';
import { coerceDecimal, currentModeOverride, computeStayCosts, stayCostStateOf, formatEuroExact } from '../utils';
import Tooltip from '../../../components/ui/Tooltip';
import type { NightTrainSearchSummary } from '../../journeys/api';
import { NIGHT_TRAIN_TOOLTIP, NightTrainStatus, nightTrainTotals, NEARBY_SEARCH_LABEL, nightRidesLabel, searchOffered, travelTimeSplit } from '../nightTrain';
import NightTrainSearchButton from './NightTrainSearchButton';

// Info-pills strip under the map (prototype jody-journey-v2.html §infopills):
// stacked rounded pills — stays + country flags + stay cost, and time ·
// modes · transport cost. The stays pill mirrors the transport pill's
// grammar (Paulo 2026-08-18); its cost is the sum of USER-ENTERED stay
// prices, exact and unstarred (the star marks estimates). Travel dates have
// their own editable section (JourneyDates); transport cost is a RANGE (sum
// of per-leg low/high bands) with a tooltip clarifying it covers tickets
// only.

interface InfoLeg {
  transportMode?: string | null;
  durationMinutes?: number | string | null;
  durationHours?: number | string | null;
  priceBandLowCents?: number | string | null;
  priceBandHighCents?: number | string | null;
  userOverrides?: Record<string, { costCents?: number; durationMinutes?: number }> | null;
  nightTrain?: { nightsOnBoard?: number } | null;
  /** Whether a night train can be searched for the leg, and what the search would find. */
  nightTrainStatus?: NightTrainStatus | null;
  nightTrainSearch?: NightTrainSearchSummary | null;
}

interface InfoPillsProps {
  stops: Array<{ country?: string; stopDurationHours?: number | string | null; stayDetails?: { costCents?: number | null } | null }>;
  legs: InfoLeg[];
  /** True while any leg is still being priced (show "estimating…"). */
  costEstimating?: boolean;
  /** False on read-only shared views: drops the "add prices" invite (a
   *  visitor can't price anything) and the edit-flavoured tooltip copy. */
  invitePricing?: boolean;
  /** Open "Search night trains nearby" for the journey (the page picks the
   *  part of the trip to start with). With it, the night pill shows on
   *  every journey the search would find a night train for, also one
   *  without night trains yet. Absent on read-only views. */
  onSearchNightTrains?: () => void;
}

const pill: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', background: '#eef1ee', borderRadius: 14, padding: '11px 14px', fontSize: 14, fontWeight: 700, color: '#16241d' };
const muted: React.CSSProperties = { color: '#6c7872', fontWeight: 500 };

// Round euros DOWN / UP to the nearest 10 for clean range bounds.
const floor10 = (eur: number) => Math.floor(eur / 10) * 10;
const ceil10 = (eur: number) => Math.ceil(eur / 10) * 10;

const InfoPills: React.FC<InfoPillsProps> = ({ stops, legs, costEstimating, invitePricing = true, onSearchNightTrains }) => {
  const placesCount = stops.length;
  const flags = Array.from(new Set(stops.map((s) => getCountryFlag(s.country)).filter((f) => f && f.trim())));
  const modes = Array.from(new Set(legs.map((l) => getTransportEmoji(l.transportMode)).filter((e) => e && e.trim())));

  // A user-entered value for a leg's CURRENT mode replaces that leg's estimate
  // (user-editable costs & times); a single user cost counts as both the low
  // and high bound of the range.
  const legMinutes = (l: InfoLeg): number => {
    const ov = currentModeOverride(l);
    if (ov?.durationMinutes != null) return Number(ov.durationMinutes);
    const m = coerceDecimal(l.durationMinutes, NaN);
    if (Number.isFinite(m)) return m;
    const h = coerceDecimal(l.durationHours, NaN);
    return Number.isFinite(h) ? h * 60 : 0;
  };
  // With night trains the moving time is split: the rides, and the hours of
  // everything else (Paulo 2026-09-30: "2 night train rides + 9h*", not one
  // big number).
  const split = travelTimeSplit(legs, legMinutes);
  const movingHours = Math.round(split.dayMinutes / 60);

  // Cost range = sum of per-leg low/high bands (in cents → euros), rounded to 10s.
  const lowCents = legs.reduce((s, l) => {
    const ov = currentModeOverride(l);
    return s + (ov?.costCents != null ? Number(ov.costCents) : coerceDecimal(l.priceBandLowCents, 0));
  }, 0);
  const highCents = legs.reduce((s, l) => {
    const ov = currentModeOverride(l);
    return s + (ov?.costCents != null ? Number(ov.costCents) : coerceDecimal(l.priceBandHighCents, 0));
  }, 0);
  const hasBands = legs.some((l) => currentModeOverride(l)?.costCents != null || Number.isFinite(coerceDecimal(l.priceBandLowCents, NaN)));
  const lowEur = floor10(lowCents / 100);
  const highEur = ceil10(highCents / 100);
  const costLabel = lowEur === highEur ? `€${highEur}` : `€${lowEur}–${highEur}`;

  // Stay-cost states (Paulo 2026-08-18): complete → exact sum; partial →
  // sum with a trailing "+" ("at least this" — Paulo 2026-09-12, matching
  // AlternativeJourneyChip) + "(2 of 5 priced)" progress nudge; none → an
  // invite to add prices (owners only — see invitePricing).
  // Omitted when the route has no overnight stay (nothing to price).
  const stays = computeStayCosts(stops);
  const staysState = stayCostStateOf(stays);
  const showStayCost = (stays.staysTotal > 0 || stays.sumCents != null)
    && !(staysState === 'none' && !invitePricing);

  // Night trains (prototype .infopill.night; Paulo 2026-09-28: facts only,
  // no hours-saved claim). Absent when the journey has none.
  const night = nightTrainTotals(legs);
  // The search is offered while it would find something for a part of the
  // trip (the backend looked when it read the journey). Where it would find
  // nothing anywhere, the pill does not invite to it: each part of the trip
  // says why on its own card.
  const canSearch = Boolean(onSearchNightTrains) && legs.some((l) => searchOffered(l));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* With night trains: what the journey has. Without: what it could
          have (owners only). Either way the search is one tap away, in the
          first thing the traveller reads (Paulo 2026-09-29). */}
      {(night.trains > 0 || canSearch) && (
        <div style={{ ...pill, background: '#ecebf7', color: '#34335c', flexWrap: 'nowrap', gap: 11 }}>
          <span aria-hidden style={{ fontSize: 20, lineHeight: 1, flex: 'none' }}>🌙</span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.15 }}>
              {night.trains > 0
                ? <>{night.trains} {night.trains === 1 ? 'night train' : 'night trains'} planned</>
                : 'Night trains'}
              <Tooltip content={NIGHT_TRAIN_TOOLTIP} widthClassName="w-64" bare>
                <button type="button" aria-label="About night trains" style={{ marginLeft: 6, background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#8584a6', fontWeight: 700, cursor: 'help' }}>ⓘ</button>
              </Tooltip>
            </span>
            <span style={{ fontWeight: 500, fontSize: 12, lineHeight: 1.3, color: '#8584a6' }}>
              {night.trains > 0 ? (
                <>
                  <b style={{ fontWeight: 700, color: '#34335c' }}>{night.nights} {night.nights === 1 ? 'night' : 'nights'} on board</b>
                  , saving <b style={{ fontWeight: 700, color: '#34335c' }}>{night.nights} hotel {night.nights === 1 ? 'night' : 'nights'}</b>
                </>
              ) : 'Sleep on the train and save a hotel night.'}
            </span>
          </span>
          {canSearch && onSearchNightTrains && (
            <span style={{ flex: 'none' }}>
              <NightTrainSearchButton
                variant="button-on-night"
                label={night.trains > 0 ? 'Find more' : 'Search'}
                ariaLabel={NEARBY_SEARCH_LABEL}
                onClick={onSearchNightTrains}
              />
            </span>
          )}
        </div>
      )}
      <div style={pill}>
        <span aria-hidden>🛏️</span>
        <b>{placesCount} {placesCount === 1 ? 'stay' : 'stays'}</b>
        {flags.length > 0 && <span style={muted}>in</span>}
        {flags.length > 0 && <span style={{ fontSize: 15, letterSpacing: 1 }}>{flags.join(' ')}</span>}
        {showStayCost && (
          <>
            <span style={muted}>costing</span>
            {staysState === 'none' ? (
              <span style={muted}>€? (add prices in each stay)</span>
            ) : (
              <Tooltip
                content={invitePricing
                  ? 'Sum of the stay prices you entered. Tap a stay to add or change them.'
                  : 'Sum of the stay prices entered for this trip.'}
                widthClassName="w-64"
              >
                <b>{formatEuroExact(stays.sumCents!)}{staysState === 'partial' ? '+' : ''}</b>
              </Tooltip>
            )}
            {staysState === 'partial' && <span style={muted}>({stays.staysPriced} of {stays.staysTotal} priced)</span>}
          </>
        )}
      </div>

      <div style={pill}>
        <span aria-hidden>{split.rides > 0 ? '🌙' : '🕐'}</span>
        {split.rides > 0 && <b>{nightRidesLabel(split.rides)}</b>}
        {split.rides > 0 && movingHours > 0 && <span style={muted}>+</span>}
        {movingHours > 0 && <b>{movingHours}h*</b>}
        {modes.length > 0 && <span style={muted}>{split.rides > 0 ? 'by' : 'moving by'}</span>}
        {modes.length > 0 && <span>{modes.join(' ')}</span>}
        <span style={muted}>costing</span>
        {hasBands && !costEstimating ? (
          <Tooltip content="Estimated cost of transport tickets only. Accommodation and extras aren't included." widthClassName="w-64">
            <b>{costLabel}*</b>
          </Tooltip>
        ) : (
          <span style={muted}>{costEstimating ? 'estimating…' : '—'}</span>
        )}
      </div>

      <div style={{ fontSize: 10.5, color: '#aab4af', paddingLeft: 2 }}>* estimated</div>
    </div>
  );
};

export default InfoPills;
